import React, { useState } from 'react';
import {
  Activity,
  ShieldCheck,
  Zap,
  Server,
  Database,
  HardDrive,
  Key,
  Cpu,
  CheckCircle2,
  RefreshCw
} from 'lucide-react';

export const DiagnosticsTab: React.FC = () => {
  const [pingMs, setPingMs] = useState<number | null>(null);
  const [isPinging, setIsPinging] = useState(false);

  const handlePing = async () => {
    setIsPinging(true);
    const start = performance.now();
    try {
      await fetch('/api/inspect/stats', { method: 'HEAD' });
      const elapsed = Math.round(performance.now() - start);
      setPingMs(elapsed);
    } catch {
      setPingMs(-1);
    } finally {
      setIsPinging(false);
    }
  };

  return (
    <div style={{ display: 'grid', gap: '1.5rem' }}>
      {/* Top Banner */}
      <div style={{
        padding: '1.25rem 1.5rem',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-card)',
        border: '1px solid var(--border-glass)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <div style={{
            padding: '10px',
            borderRadius: 'var(--radius-md)',
            background: 'rgba(16, 185, 129, 0.2)',
            color: 'var(--accent-emerald)'
          }}>
            <ShieldCheck size={24} />
          </div>
          <div>
            <h2 style={{ fontSize: '1.15rem', fontWeight: 800, color: '#fff', margin: 0 }}>
              System & Quota Safety Guard
            </h2>
            <p style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: '2px' }}>
              SoulSync Architecture & Cloudflare Worker Free Tier Protection Status
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handlePing}
          disabled={isPinging}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '8px 16px',
            borderRadius: 'var(--radius-md)',
            border: '1px solid var(--border-glass)',
            background: 'var(--bg-glass)',
            color: '#fff',
            fontSize: '0.82rem',
            fontWeight: 600,
            cursor: isPinging ? 'not-allowed' : 'pointer'
          }}
        >
          <Zap size={14} color="var(--accent-amber)" />
          <span>{isPinging ? 'Testing...' : pingMs !== null ? `${pingMs}ms Latency` : 'Test Worker Ping'}</span>
        </button>
      </div>

      {/* Cloudflare Bindings Grid */}
      <div>
        <h3 style={{ fontSize: '0.95rem', fontWeight: 800, color: '#fff', marginBottom: '0.85rem' }}>
          Cloudflare Pages Bindings Status
        </h3>
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: '1rem'
        }}>
          {[
            {
              icon: <Database size={18} color="var(--accent-purple)" />,
              name: 'SOULSYNC_DB',
              type: 'Cloudflare D1 (SQLite)',
              detail: 'faed61eb-1cc4-40a6-b25b-a9050497b44c',
              status: 'Bound & Healthy'
            },
            {
              icon: <HardDrive size={18} color="var(--accent-coral)" />,
              name: 'SOULSYNC_MEDIA',
              type: 'Cloudflare R2 Object Storage',
              detail: 'soulsync-media',
              status: 'Bound & Healthy'
            },
            {
              icon: <Key size={18} color="var(--accent-cyan)" />,
              name: 'SOULSYNC_CHALLENGES',
              type: 'Cloudflare KV Namespace',
              detail: 'f3f575287c6d40428b2696dac1246f87',
              status: 'Bound & Healthy'
            },
            {
              icon: <Cpu size={18} color="var(--accent-emerald)" />,
              name: 'MODAL_BAKER / DEMUCS',
              type: 'Modal Labs NVIDIA T4 GPU Container',
              detail: 'cascalhosamarelos--soulsync-baker',
              status: 'Configured'
            }
          ].map(b => (
            <div
              key={b.name}
              style={{
                padding: '1.1rem',
                borderRadius: 'var(--radius-lg)',
                background: 'var(--bg-card)',
                border: '1px solid var(--border-glass)',
                display: 'flex',
                flexDirection: 'column',
                gap: '8px'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {b.icon}
                  <span style={{ fontWeight: 700, color: '#fff', fontSize: '0.85rem', fontFamily: 'var(--font-mono)' }}>
                    {b.name}
                  </span>
                </div>
                <span style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                  fontSize: '0.72rem',
                  color: 'var(--accent-emerald)',
                  fontWeight: 600
                }}>
                  <CheckCircle2 size={12} />
                  <span>{b.status}</span>
                </span>
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{b.type}</div>
              <code style={{ fontSize: '0.7rem', color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', wordBreak: 'break-all' }}>
                {b.detail}
              </code>
            </div>
          ))}
        </div>
      </div>

      {/* Quota Safeguards Checklist */}
      <div style={{
        padding: '1.25rem',
        borderRadius: 'var(--radius-lg)',
        background: 'var(--bg-card)',
        border: '1px solid var(--border-glass)'
      }}>
        <h3 style={{ fontSize: '0.95rem', fontWeight: 800, color: '#fff', marginBottom: '1rem' }}>
          Active Anti-Machinegun & Rate-Limit Safeguards
        </h3>

        <div style={{ display: 'grid', gap: '10px' }}>
          {[
            {
              title: 'Zero Background Polling on Inspector',
              desc: 'No setInterval or automatic continuous re-fetching. Data only refreshes when the user explicitly clicks the Refresh button.',
              active: true
            },
            {
              title: 'Concurrency Mutex Protection (isFetchingRef)',
              desc: 'Rapid clicking on buttons or tab switching is blocked by an in-flight mutex lock, dropping redundant requests.',
              active: true
            },
            {
              title: '100% In-Memory Search & Filtering',
              desc: 'Client-side searching by title, user, or status uses pre-loaded dataset filters without firing single D1 row reads.',
              active: true
            },
            {
              title: 'Enforced Pagination Capping (LIMIT 50)',
              desc: 'All table rows and query runs enforce LIMIT 50 to prevent large row-read consumption against Cloudflare free quotas.',
              active: true
            },
            {
              title: 'Direct R2 Asset Serving',
              desc: 'Media playback (MP4 video, MP3 stems) queries R2 public bucket endpoints directly to conserve Cloudflare Worker invocations.',
              active: true
            }
          ].map((rule, idx) => (
            <div
              key={idx}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '12px',
                padding: '10px 14px',
                borderRadius: 'var(--radius-md)',
                background: 'var(--bg-glass)',
                border: '1px solid var(--border-glass)'
              }}
            >
              <CheckCircle2 size={16} color="var(--accent-emerald)" style={{ flexShrink: 0, marginTop: '2px' }} />
              <div>
                <div style={{ fontWeight: 700, color: '#fff', fontSize: '0.82rem' }}>
                  {rule.title}
                </div>
                <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: '2px', lineHeight: 1.4 }}>
                  {rule.desc}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};
